import {getFormResultConfig, getFormResultAnswers, getFormResultDate, getStoredFormAnswerLabel} from "./formResultHelpers.js";

function getScoreSignature(inventory = {}) {
	return JSON.stringify({
		questions: (inventory.questions || []).map(question => [question.id, question.text]).sort(([a], [b]) => String(a).localeCompare(String(b))),
		options: (inventory.options || []).map(option => [option.id, option.title, Number(option.value)]).sort(([a], [b]) => String(a).localeCompare(String(b))),
		totalQuestionIds: [...(inventory.totalQuestionIds || [])].sort(),
		domains: (inventory.domains || []).map(domain => [domain.id, domain.title, [...(domain.questionIds || [])].sort()]).sort(([a], [b]) => String(a).localeCompare(String(b))),
	})
}
export function getFrequencyInventoryConfigErrors(inventory = {}) {
	const questions = inventory.questions || []
	const options = inventory.options || []
	const domains = inventory.domains || []
	if (!Array.isArray(questions) || !Array.isArray(options) || !Array.isArray(domains) || !options.length) return ["Invalid inventory structure"]
	if (!inventory.instructions) return ["Inventory instructions are missing"]
	const unique = items => new Set(items.map(item => item.id)).size === items.length && items.every(item => item.id)
	if (!unique(questions) || !unique(options) || !unique(domains)) return ["Inventory IDs must be unique"]
	if (questions.some(question => !question.text) || options.some(option => !option.title || option.value === "" || option.value === null || option.value === undefined || !Number.isFinite(Number(option.value)) || Number(option.value) < 0) || domains.some(domain => !domain.title)) return ["Inventory wording or values are missing"]
	const values = options.map(option => Number(option.value))
	if (new Set(values).size !== values.length) return ["Rating values must be distinct"]
	const ids = new Set(questions.map(question => question.id))
	if (!Array.isArray(inventory.totalQuestionIds) || inventory.totalQuestionIds.some(id => !ids.has(id)) || new Set(inventory.totalQuestionIds).size !== inventory.totalQuestionIds.length) return ["Total scoring refers to a removed or repeated question"]
	if (domains.some(domain => !Array.isArray(domain.questionIds) || domain.questionIds.some(id => !ids.has(id)) || new Set(domain.questionIds).size !== domain.questionIds.length)) return ["A domain refers to a removed or repeated question"]
	return []
}

export function getFrequencyInventoryReport(inventory = {}, answers = {}) {
	const questions = inventory.questions || []
	const options = inventory.options || []
	const optionById = new Map(options.map(option => [option.id, option]))
	const missing = questions.filter(question => !optionById.has(answers[question.id]))
	const totalIds = inventory.totalQuestionIds || questions.map(question => question.id)
	const missingTotalIds = totalIds.filter(id => !optionById.has(answers[id]))
	const sum = ids => ids.reduce((total, id) => total + Number(optionById.get(answers[id]).value), 0)
	const maximum = options.length ? Math.max(...options.map(option => Number(option.value))) : 0
	const domainScores = (inventory.domains || []).map(domain => {
		const missingIds = domain.questionIds.filter(id => !optionById.has(answers[id]))
		return {id: domain.id, title: domain.title, value: missingIds.length || !domain.questionIds.length ? null : sum(domain.questionIds), max: domain.questionIds.length * maximum, missingIds}
	})
	return {
		answeredCount: questions.length - missing.length,
		questionCount: questions.length,
		missing: missing.map((question, index) => ({id: question.id, number: questions.indexOf(question) + 1, text: question.text})),
		total: missingTotalIds.length || !totalIds.length ? null : sum(totalIds),
		totalMax: totalIds.length * maximum,
		domainScores,
	}
}

export function getFrequencyInventoryPayloadErrors(payload = {}, config = {}) {
	const inventory = config.frequencyInventory || {}
	const configErrors = getFrequencyInventoryConfigErrors(inventory)
	if (configErrors.length) return configErrors
	const answers = payload.answers || {}
	const questionIds = new Set(inventory.questions.map(question => question.id))
	const optionIds = new Set(inventory.options.map(option => option.id))
	if (Object.keys(answers).some(id => !questionIds.has(id) || !optionIds.has(answers[id]))) return ["A response refers to a removed question or rating option"]
	const report = getFrequencyInventoryReport(inventory, answers)
	if (!report.questionCount) return ["Add a question before collecting responses"]
	if (!inventory.allowPartial && report.missing.length) return [`Unanswered items: ${report.missing.map(item => item.number).join(", ")}`]
	return []
}

export function getFrequencyInventoryResultView(config, result = {}, priorResults = []) {
	const storedConfig = getFormResultConfig(undefined, result)
	config = storedConfig.frequencyInventory ? storedConfig : getFormResultConfig(config, result)
	const inventory = config.frequencyInventory || {questions: [], options: [], domains: []}
	const answers = getFormResultAnswers(result)
	const report = getFrequencyInventoryReport(inventory, answers)
	const previousInventoryForComparison = storedConfig.frequencyInventory
	const history = priorResults.filter(prior => prior.toolType === result.toolType && (!result.id || prior.id !== result.id)).map((prior, index) => {
		const priorInventory = getFormResultConfig(undefined, prior).frequencyInventory
		const comparable = !!previousInventoryForComparison && !!priorInventory && getScoreSignature(previousInventoryForComparison) === getScoreSignature(priorInventory)
		const previous = priorInventory ? getFrequencyInventoryReport(priorInventory, getFormResultAnswers(prior)) : prior.summary?.frequencyReport || prior.enginePayload?.frequencyReport || getFrequencyInventoryReport({}, {})
		const domains = previous.domainScores.map(domain => {
			const current = report.domainScores.find(item => item.id === domain.id)
			return {...domain, delta: !comparable || current?.value == null || domain.value == null ? null : current.value - domain.value}
		})
		return {key: prior.id || index, date: getFormResultDate(prior, "completedDate"),
			answered: `${previous.answeredCount}/${previous.questionCount} ${inventory.answeredLabel}`,
			total: previous.total === null ? inventory.withheldLabel : `${previous.total}/${previous.totalMax}`,
			delta: !comparable || previous.total === null || report.total === null ? null : report.total - previous.total,
			domains, withheldLabel: inventory.withheldLabel, isAdapted: priorInventory.isAdapted}
	})
	const previousInventory = getFormResultConfig(undefined, result).frequencyInventory || {}
	const previousOptions = previousInventory.options || []
	const activeIds = new Set((inventory.questions || []).map(item => item.id))
	const archivedItems = (previousInventory.questions || []).filter(item => !activeIds.has(item.id) && answers[item.id] !== undefined && answers[item.id] !== null && answers[item.id] !== "").map(item => ({...item, answerLabel: getStoredFormAnswerLabel(inventory.options, answers[item.id], previousOptions, inventory.unansweredLabel)}))
	const optionById = new Map((inventory.options || []).map(option => [option.id, option]))
	const answerLabels = Object.fromEntries((inventory.questions || []).map(item => {
		const option = optionById.get(answers[item.id])
		const label = option ? `${option.title} (${option.value})` : getStoredFormAnswerLabel(inventory.options, answers[item.id], previousOptions, inventory.unansweredLabel)
		return [item.id, label]
	}))
	return {config, inventory, answers, archivedItems, report, history, answerLabels, completedDate: getFormResultDate(result, "completedDate")}
}
